import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Grid,
    ImageList,
    ImageListItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const InventoryDetailsDialog = ({ open, onClose, center }) => {
    if (!center) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Inventory Details
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#888',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: '#555',
                        },
                    },
                }}>
                    <Box sx={{ p: 3 }}>
                        {/* Photos Grid */}
                        {center.photos && center.photos.length > 0 && (
                            <Grid item xs={12} sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                                    Photos
                                </Typography>
                                <Grid container spacing={2}>
                                    {center.photos.map((photo, index) => (
                                        <Grid item xs={3} key={index} sx={{ position: 'relative' }}>
                                            <Box
                                                component="img"
                                                src={photo.url}
                                                alt={photo.name || `Photo ${index + 1}`}
                                                sx={{
                                                    width: '100%',
                                                    height: 120,
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        )}

                        <Grid container spacing={3}>
                            {/* Basic Information */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                                    Basic Information
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Center Name</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{center.centerName || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Building Type</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{center.buildingType || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Building Grade</Typography>
                                        <Typography variant="body1">{center.buildingGrade || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Active</Typography>
                                        <Typography variant="body1">{center.active ? 'Yes' : 'No'}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Location Information */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                                    Location Information
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Address</Typography>
                                        <Typography variant="body1" sx={{
                                            p: 1.5,
                                            bgcolor: 'grey.50',
                                            borderRadius: 1
                                        }}>{center.address || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>City</Typography>
                                        <Typography variant="body1">{center.city || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Locality</Typography>
                                        <Typography variant="body1">{center.locality || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Google Maps Location</Typography>
                                        <Typography variant="body1" sx={{
                                            p: 1.5,
                                            bgcolor: 'grey.50',
                                            borderRadius: 1,
                                            wordBreak: 'break-all'
                                        }}>{center.googleMapsLocation || 'N/A'}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Contact Information */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                                    Contact Information
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Center Manager Name</Typography>
                                        <Typography variant="body1">{center.centerManagerName || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Center Manager Phone</Typography>
                                        <Typography variant="body1">{center.centerManagerPhone || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Center Manager Email</Typography>
                                        <Typography variant="body1">{center.centerManagerEmail || 'N/A'}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Additional Information */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                                    Additional Information
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Operational Hours</Typography>
                                        <Typography variant="body1">{center.operationalHours || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Operational From</Typography>
                                        <Typography variant="body1">{center.operationalFrom || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>List Price</Typography>
                                        <Typography variant="body1">₹{center.pricePerSeat || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Cafeteria Capacity</Typography>
                                        <Typography variant="body1">{center.cafeteriaCapacity || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Car Parking Cost</Typography>
                                        <Typography variant="body1">{center.carParkingCost || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Bike Parking Cost</Typography>
                                        <Typography variant="body1">{center.bikeParkingCost || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Description</Typography>
                                        <Typography variant="body1" sx={{
                                            p: 1.5,
                                            bgcolor: 'grey.50',
                                            borderRadius: 1,
                                            minHeight: '60px'
                                        }}>{center.description || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Amenities</Typography>
                                        <Typography variant="body1" sx={{
                                            p: 1.5,
                                            bgcolor: 'grey.50',
                                            borderRadius: 1
                                        }}>
                                            {center.amenities && center.amenities.length > 0
                                                ? center.amenities
                                                    .map(amenity => amenity.trim())
                                                    .filter(Boolean)
                                                    .join(', ')
                                                : 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default InventoryDetailsDialog;
